import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquare, CheckCircle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/hooks/useAuth";

// ── Types ──────────────────────────────────────────────────────────────────

interface FeedbackDrawerProps {
  open: boolean;
  onClose: () => void;
}

// ── FeedbackDrawer ─────────────────────────────────────────────────────────

export function FeedbackDrawer({ open, onClose }: FeedbackDrawerProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [whatDoing, setWhatDoing]       = useState("");
  const [whatHappened, setWhatHappened] = useState("");
  const [errorMsg, setErrorMsg]         = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [submitError, setSubmitError]   = useState("");

  // Reset form each time the drawer opens
  useEffect(() => {
    if (open) {
      setWhatDoing("");
      setWhatHappened("");
      setErrorMsg("");
      setSubmitted(false);
      setSubmitError("");
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!whatDoing.trim() || !whatHappened.trim()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const { error } = await supabase.from("test_feedback").insert({
        user_id:          user?.id   ?? null,
        user_email:       user?.email ?? null,
        page_url:         window.location.pathname,
        what_i_was_doing: whatDoing.trim(),
        what_happened:    whatHappened.trim(),
        error_message:    errorMsg.trim() || null,
      });
      if (error) throw error;
      setSubmitted(true);
      // Auto-close after 2.5 seconds
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (err: any) {
      setSubmitError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="overflow-y-auto w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare size={18} className="text-amber-600" />
            {t('feedback.title')}
          </SheetTitle>
        </SheetHeader>

        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <CheckCircle size={40} className="text-green-500" />
            <p className="font-medium text-gray-800">{t('feedback.thankYou')}</p>
            <p className="text-sm text-gray-500">{t('feedback.submitted')}</p>
          </div>
        ) : (
          <div className="space-y-5 py-6">
            <div className="space-y-1.5">
              <Label htmlFor="fb-doing">{t('feedback.whatTrying')}</Label>
              <Textarea
                id="fb-doing"
                value={whatDoing}
                onChange={(e) => setWhatDoing(e.target.value)}
                placeholder={t('feedback.whatTryingPlaceholder')}
                rows={3}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fb-happened">{t('feedback.whatHappened')}</Label>
              <Textarea
                id="fb-happened"
                value={whatHappened}
                onChange={(e) => setWhatHappened(e.target.value)}
                placeholder={t('feedback.whatHappenedPlaceholder')}
                rows={3}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fb-error">{t('feedback.errorMessage')}</Label>
              <Textarea
                id="fb-error"
                value={errorMsg}
                onChange={(e) => setErrorMsg(e.target.value)}
                placeholder={t('feedback.errorPlaceholder')}
                rows={2}
              />
            </div>

            <p className="text-xs text-gray-400">
              {t('feedback.submittingFrom', { pathname: window.location.pathname })}
            </p>

            {submitError && (
              <p className="text-sm text-red-500">{submitError}</p>
            )}

            <Button
              onClick={handleSubmit}
              disabled={submitting || !whatDoing.trim() || !whatHappened.trim()}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            >
              {submitting ? t('feedback.sending') : t('feedback.submit')}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── FeedbackButton (Floating Action Button) ────────────────────────────────

export function FeedbackButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        style={{
          position:     "fixed",
          bottom:       "84px",   // sits 60 px above the green HelpButton at 24px
          right:        "24px",
          zIndex:       1000,
          width:        "48px",
          height:       "48px",
          borderRadius: "50%",
          background:   "#d97706",  // amber-600
          color:        "#fff",
          border:       "none",
          cursor:       "pointer",
          display:      "flex",
          alignItems:   "center",
          justifyContent: "center",
          boxShadow:    "0 4px 14px rgba(217, 119, 6, 0.45)",
        }}
        onClick={() => setOpen(true)}
        aria-label="Report feedback"
        title="Report an Issue / Give Feedback"
      >
        <MessageSquare size={20} />
      </button>

      <FeedbackDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}
